import type { OutcomeDistributionByScore } from "@repo/app-types/entities/teammatePerformanceSummary";
import { renderPlural, Size } from "@repo/ui/helpers/helpers";
import { Label } from "@repo/ui/Label/label";
import { AnchoredTooltip } from "@repo/ui/tooltip/anchoredTooltip";
import type { FC, ReactNode } from "react";
import { useLayoutEffect, useMemo, useRef, useState } from "react";

// Chart layout constants
const PADDING = { left: 30, right: 10, top: 10, bottom: 32 };

// Score keys by grading type
const scoreKeys = {
  yes_no: ["no", "yes"],
  "1_5_score": ["1", "2", "3", "4", "5"],
} as const;

/** Capitalize first letter for display (e.g., "no" → "No", "1" → "1") */
const toLabel = (key: string) => key.charAt(0).toUpperCase() + key.slice(1);

/** Key used for N/A criteria scores in the distribution (matches server-side constant) */
const NA_CRITERIA_SCORE_KEY = "N/A";

interface CallVolumeByCriteriaScoreChartProps {
  /** The distribution for the specific criterion being displayed */
  criterionDistribution: OutcomeDistributionByScore | undefined;
  /** The grading value type - determines whether to show Yes/No or 1-5 on the X-axis */
  gradingValueType: "yes_no" | "1_5_score";
}

export const CallVolumeByCriteriaScoreChart: FC<
  CallVolumeByCriteriaScoreChartProps
> = ({ criterionDistribution, gradingValueType }) => {
  const svgContainerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(400);
  const [height, setHeight] = useState(100);
  // SVG shapes can't be React Aria tooltip triggers, so the chart tracks the
  // hovered bar itself and anchors one tooltip to it.
  const hoveredHitboxRef = useRef<SVGRectElement | null>(null);
  const [hoveredTooltipContent, setHoveredTooltipContent] =
    useState<ReactNode>(null);

  // Build chart data from criterion distribution (including ALL calls for N/A count)
  const chartData = useMemo(() => {
    if (!criterionDistribution) return [];

    const scoreData = scoreKeys[gradingValueType].map((scoreKey) => {
      const distribution = criterionDistribution[scoreKey] ?? {};

      // Calculate total for this score (ALL outcomes, including N/A outcomes)
      let total = 0;
      for (const count of Object.values(distribution)) {
        total += count ?? 0;
      }

      return { score: toLabel(scoreKey), count: total, isNa: false };
    });

    // Calculate N/A count directly from the distribution (ALL outcomes)
    const naDistribution = criterionDistribution[NA_CRITERIA_SCORE_KEY] ?? {};
    let naCount = 0;
    for (const count of Object.values(naDistribution)) {
      naCount += count ?? 0;
    }

    // Add N/A bar at the end
    return [...scoreData, { score: "N/A", count: naCount, isNa: true }];
  }, [criterionDistribution, gradingValueType]);

  // Calculate total calls for percentage calculation
  const totalCalls = useMemo(
    () => chartData.reduce((sum, d) => sum + d.count, 0),
    [chartData],
  );

  // Add percentage to chart data
  const chartDataWithPercentages = useMemo(
    () =>
      chartData.map((d) => ({
        ...d,
        percentage: calculatePercentage(d.count, totalCalls),
      })),
    [chartData, totalCalls],
  );

  // Check if there's any data to show
  const hasData = chartDataWithPercentages.some((point) => point.count > 0);

  // Calculate max percentage for Y-axis scale
  const maxPercentage = useMemo(
    () => getMaxPercentage(chartDataWithPercentages),
    [chartDataWithPercentages],
  );

  // Handle container resize
  useLayoutEffect(() => {
    const container = svgContainerRef.current;
    if (!container) return;

    const updateSize = () => {
      setWidth(container.clientWidth);
      setHeight(container.clientHeight);
    };
    updateSize();

    const observer = new ResizeObserver(updateSize);
    observer.observe(container);
    return () => observer.disconnect();
  }, [hasData]);

  if (!hasData) {
    return null;
  }

  // Calculate chart dimensions
  const chartWidth = width - PADDING.left - PADDING.right;
  const chartHeight = height - PADDING.top - PADDING.bottom;
  const barWidth = chartWidth / chartDataWithPercentages.length;
  const barGap = barWidth * 0.25;
  const actualBarWidth = barWidth - barGap;

  return (
    <div className="space-y-2">
      <Label size={Size.SMALL}>Call volume by criteria score</Label>

      {/* Chart */}
      <div
        ref={svgContainerRef}
        className="relative w-full"
        style={{ height: "115px" }}
      >
        <svg
          className="absolute size-full"
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="xMidYMid meet"
          style={{ overflow: "visible" }}
        >
          {/* Grid lines */}
          <line
            x1={PADDING.left}
            y1={PADDING.top + chartHeight}
            x2={width - PADDING.right}
            y2={PADDING.top + chartHeight}
            stroke="#e5e7eb"
            strokeWidth="1"
          />
          <line
            x1={PADDING.left}
            y1={PADDING.top}
            x2={width - PADDING.right}
            y2={PADDING.top}
            stroke="#e5e7eb"
            strokeWidth="1"
            strokeDasharray="3 3"
          />

          {/* Bars */}
          {chartDataWithPercentages.map((dataPoint, barIndex) => {
            const x = PADDING.left + barIndex * barWidth + barGap / 2;

            // Height based on percentage relative to max percentage
            const barHeight = calculateBarHeight(
              dataPoint.percentage,
              maxPercentage,
              chartHeight,
            );
            const y = PADDING.top + chartHeight - barHeight;

            // Use gray for N/A bar, blue for others
            const barFill = dataPoint.isNa
              ? "var(--color-gray-300)"
              : "var(--color-blue-300)";
            const barStroke = dataPoint.isNa
              ? "var(--color-gray-400)"
              : "var(--color-blue-400)";

            return (
              <g key={dataPoint.score} className="cursor-default">
                {/* Invisible hitbox covering bar area + label */}
                <rect
                  x={x}
                  y={PADDING.top}
                  width={actualBarWidth}
                  height={chartHeight + 20}
                  fill="transparent"
                  onMouseEnter={(event) => {
                    hoveredHitboxRef.current = event.currentTarget;
                    setHoveredTooltipContent(renderTooltipContent(dataPoint));
                  }}
                  onMouseLeave={() => setHoveredTooltipContent(null)}
                />

                {/* Visible bar */}
                <rect
                  x={x}
                  y={y}
                  width={actualBarWidth}
                  height={Math.max(barHeight, 0)}
                  strokeWidth="1"
                  rx={2}
                  ry={2}
                  className="pointer-events-none"
                  fill={barFill}
                  stroke={barStroke}
                />

                {/* X-axis label */}
                <text
                  x={x + actualBarWidth / 2}
                  y={height - PADDING.bottom + 14}
                  textAnchor="middle"
                  className="pointer-events-none fill-gray-500 text-[10px] font-medium"
                >
                  {dataPoint.score}
                </text>
              </g>
            );
          })}

          {/* Y-axis labels */}
          <text
            x={PADDING.left - 8}
            y={PADDING.top + 4}
            textAnchor="end"
            className="fill-gray-500 text-[10px] font-medium"
          >
            {formatPercentage(maxPercentage)}%
          </text>
          <text
            x={PADDING.left - 8}
            y={PADDING.top + chartHeight}
            textAnchor="end"
            className="fill-gray-500 text-[10px] font-medium"
          >
            0%
          </text>

          {/* X-axis title */}
          <text
            x={PADDING.left + chartWidth / 2}
            y={height - 4}
            textAnchor="middle"
            className="fill-gray-500 text-[10px] font-medium"
          >
            Criteria grade
          </text>
        </svg>

        {hoveredTooltipContent !== null ? (
          <AnchoredTooltip triggerRef={hoveredHitboxRef}>
            {hoveredTooltipContent}
          </AnchoredTooltip>
        ) : null}
      </div>
    </div>
  );
};

// Helper functions for percentage calculations

/**
 * Calculates the percentage of a count relative to a total.
 * Returns 0 if total is 0 or less.
 */
function calculatePercentage(count: number, total: number): number {
  if (total <= 0) {
    return 0;
  }
  return (count / total) * 100;
}

/**
 * Gets the maximum percentage from an array of data points.
 * Returns at least 1 to ensure the chart has a valid scale.
 */
function getMaxPercentage(dataPoints: { percentage: number }[]): number {
  return Math.max(...dataPoints.map((d) => d.percentage), 1);
}

/**
 * Calculates the bar height based on percentage relative to max percentage.
 */
function calculateBarHeight(
  percentage: number,
  maxPercentage: number,
  chartHeight: number,
): number {
  return (percentage / maxPercentage) * chartHeight;
}

/**
 * Formats a percentage value for display by rounding to the nearest integer.
 */
function formatPercentage(percentage: number): number {
  return Math.round(percentage);
}

/**
 * Renders tooltip content for a data point.
 */
function renderTooltipContent(dataPoint: {
  score: string;
  count: number;
  percentage: number;
  isNa: boolean;
}): ReactNode {
  const baseContent = (
    <div>
      <strong>{dataPoint.score}</strong>:{" "}
      {formatPercentage(dataPoint.percentage)}% (
      {renderPlural(dataPoint.count, {
        one: "1 call",
        other: (count) => `${count.toLocaleString()} calls`,
      })}
      )
    </div>
  );

  if (dataPoint.isNa) {
    return (
      <>
        {baseContent}
        <div className="mt-1 max-w-[200px] text-gray-400">
          Criteria are not graded when they are irrelevant or aren&apos;t
          reached during a call.
        </div>
      </>
    );
  }

  return baseContent;
}
